import { useId, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ImageOff, ImageUp, Trash2 } from 'lucide-react';
import { getErrorMessage, keys } from '@blimo/api-client';
import { useApi } from '@blimo/api-client/react';
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle, toast } from '@blimo/ui';

const ACCEPT = 'image/jpeg,image/png,image/webp,image/gif';

/**
 * Bild der Mitteilung (F-501): Vorschau; mit `canManage` hochladen/ersetzen/entfernen. Eigener Endpunkt, sofort gespeichert.
 * Typ/Größe (JPEG/PNG/WebP/GIF, max. 10 MB) und Recht (`announcements.manage`) prüft der Server.
 * @param {{ announcement: { id: number, title?: string, has_image?: boolean, image_version?: number|null }, canManage: boolean }} props
 */
export function AnnouncementImageCard({ announcement, canManage }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const inputRef = useRef(/** @type {HTMLInputElement|null} */ (null));
  const id = useId();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(/** @type {string|null} */ (null));

  /** @param {any} updated */
  const apply = (updated) => {
    queryClient.setQueryData(keys.admin.detail('announcements', announcement.id), (previous) => ({ ...previous, ...updated }));
    queryClient.invalidateQueries({ queryKey: ['blimo', 'admin', 'announcements', 'list'] });
  };

  /** @param {File|undefined} file */
  async function upload(file) {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      apply(await api.admin.announcements.uploadImage(announcement.id, file));
      toast.success('Bild gespeichert');
    } catch (e) {
      setError(getErrorMessage(e, 'Das Bild konnte nicht gespeichert werden.'));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  async function remove() {
    setBusy(true);
    setError(null);
    try {
      await api.admin.announcements.removeImage(announcement.id);
      apply({ has_image: false, image_version: null, image_document_id: null });
      toast.success('Bild entfernt');
    } catch (e) {
      setError(getErrorMessage(e, 'Das Bild konnte nicht entfernt werden.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle as="h2">Bild</CardTitle>
        <CardDescription id={`${id}-hint`}>
          {canManage ? 'JPEG, PNG, WebP oder GIF, max. 10 MB. Wird sofort gespeichert und im Portal angezeigt.' : 'Wird im Portal zur Mitteilung angezeigt.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <div className="flex aspect-[4/3] w-full items-center justify-center overflow-hidden rounded-md border bg-muted">
          {announcement.has_image ? (
            <img
              src={api.admin.announcements.imageUrl(announcement.id, announcement.image_version)}
              alt={`Bild ${announcement.title ?? ''}`.trim()}
              className="size-full object-cover"
            />
          ) : (
            <span className="flex flex-col items-center gap-1 text-xs text-muted-foreground">
              <ImageOff className="size-6" aria-hidden="true" /> Kein Bild
            </span>
          )}
        </div>
        {canManage && (
          <div className="flex flex-wrap gap-2">
            <input
              ref={inputRef}
              id={`${id}-file`}
              type="file"
              accept={ACCEPT}
              className="sr-only"
              onChange={(e) => upload(e.target.files?.[0])}
              aria-label="Bilddatei auswählen"
              aria-describedby={`${id}-hint`}
            />
            <Button type="button" size="sm" variant="outline" loading={busy} onClick={() => inputRef.current?.click()}>
              <ImageUp aria-hidden="true" /> {announcement.has_image ? 'Bild ersetzen' : 'Bild hochladen'}
            </Button>
            {announcement.has_image && (
              <Button type="button" size="sm" variant="ghost" disabled={busy} onClick={remove}>
                <Trash2 aria-hidden="true" /> Bild entfernen
              </Button>
            )}
          </div>
        )}
        {error && (
          <p role="alert" className="text-xs font-medium text-destructive">
            {error}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
